import React from "react";
import { Phone, BatteryLow, Clock3, AlertOctagon, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { COMPANY, HERO_IMAGES } from "../mock/data";

export function ProblemSection() {
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div className="order-2 lg:order-1">
          <img src={HERO_IMAGES.slow} alt="Slow phone performance" className="rounded-2xl shadow-xl w-full h-[380px] object-cover" />
        </div>
        <div className="order-1 lg:order-2">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">
            Stop Paying the Price for <span className="text-rose-600">Poor Protection</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Traditional antivirus apps drain your battery, slow down your phone, and constantly interrupt you with annoying popups. You deserve better.
          </p>
          <ul className="mt-6 space-y-3">
            <Item icon={<BatteryLow className="h-4 w-4" />} text="Battery dies in 4 hours" />
            <Item icon={<Clock3 className="h-4 w-4" />} text="Apps take forever to load" />
            <Item icon={<AlertOctagon className="h-4 w-4" />} text="Constant security warnings" />
          </ul>
          <Button asChild className="mt-8 bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}

function Item({ icon, text }) {
  return (
    <li className="flex items-center gap-3 text-slate-700">
      <span className="h-8 w-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">{icon}</span>
      <span className="font-medium">{text}</span>
    </li>
  );
}

export function PeaceOfMindSection() {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-white to-emerald-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="section-title text-3xl md:text-4xl font-extrabold text-slate-900">
            Never Worrying About Your Phone <span className="text-emerald-600">Again</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Picture this: you&apos;re at a coffee shop, checking your bank account on your phone. With {COMPANY.brand}, you browse with confidence, knowing your financial data is protected. No more anxiety about malware or hackers.
          </p>
          <ul className="mt-6 space-y-3">
            <Peace text="Browse the web without fear" />
            <Peace text="Keep your family's data safe" />
            <Peace text="Enjoy your phone, not fight it" />
          </ul>
          <Button asChild className="mt-8 bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}
            </a>
          </Button>
        </div>
        <div>
          <img src={HERO_IMAGES.happy} alt="Happy person using phone with confidence" className="rounded-2xl shadow-xl w-full h-[380px] object-cover" />
        </div>
      </div>
    </section>
  );
}

function Peace({ text }) {
  return (
    <li className="flex items-center gap-3 text-slate-700">
      <span className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <CheckCircle2 className="h-4 w-4" />
      </span>
      <span className="font-medium">{text}</span>
    </li>
  );
}

export function ComparisonSection() {
  const before = [
    "Sluggish UI and slow performance",
    "Battery drains quickly",
    "Constant malware risks",
    "Storage cluttered with junk",
    "Privacy concerns"
  ];
  const after = [
    "Lightning-fast performance",
    "Optimized battery life",
    "24/7 malware protection",
    "Clean, organized storage",
    "Complete privacy protection"
  ];

  return (
    <section className="py-16 md:py-20 bg-slate-950 text-white relative overflow-hidden">
      <div className="blob bg-emerald-500/20 h-80 w-80 -top-20 left-1/3" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="section-title text-3xl md:text-4xl font-extrabold">Tired of Slow, Vulnerable Phones?</h2>
          <p className="mt-4 text-slate-300">See the transformation from a vulnerable, sluggish device to a fast, protected, and secure Android phone.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-8">
            <p className="text-rose-300 text-sm font-semibold uppercase tracking-widest">Before {COMPANY.brand}</p>
            <ul className="mt-4 space-y-3">
              {before.map((b) => (
                <li key={b} className="flex items-start gap-2 text-slate-200">
                  <XCircle className="h-5 w-5 mt-0.5 text-rose-400 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8">
            <p className="text-emerald-300 text-sm font-semibold uppercase tracking-widest">With {COMPANY.brand}</p>
            <ul className="mt-4 space-y-3">
              {after.map((a) => (
                <li key={a} className="flex items-start gap-2 text-slate-100">
                  <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-400 shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-10 text-center">
          <p className="text-slate-300">Need help securing your phone? Talk to an expert now and get protected in minutes.</p>
          <Button asChild className="mt-4 bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Phone className="h-4 w-4 mr-2" /> Call {COMPANY.phone}
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
